import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuth";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

export default function PortalLogin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { requestOtp, verify, me } = usePortalAuth();

  const [step, setStep] = useState("identifier");
  const [society, setSociety] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/society/${slug}`);
        setSociety(data);
      } catch (e) {
        toast.error(apiErr(e));
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (me?.person) nav(`/portal/${slug}`);
  }, [me, slug, nav]);

  const sendOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await requestOtp(slug, identifier);
      setMockOtp(r.otp_mock || "");
      toast.success("OTP sent (mock)");
      setStep("otp");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await verify(slug, identifier, otp);
      toast.success(`Welcome, ${p.name || "member"}`);
      nav(`/portal/${slug}`);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (!society) return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center px-6">
      <div className="text-stone-500">Loading society…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand-50 flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-6 py-6"><BrandMark variant="portal" size={44} /></div>
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-center px-16 xl:px-24 bg-sand-200 border-r border-stone-200">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">Society portal</div>
          <h1 className="editorial text-5xl leading-tight tracking-tight">Welcome to<br/><span className="italic text-terracotta">{society.name}</span>.</h1>
          <p className="mt-6 text-stone-600 max-w-md leading-relaxed">Sign in with your registered phone number or email. We&apos;ll send a one-time password. No app to install.</p>
          {society.registration_number && <p className="mt-8 text-xs text-stone-500 uppercase tracking-widest">Reg. {society.registration_number}</p>}
        </div>

        <div className="flex items-center justify-center px-6 py-14">
          <div className="w-full max-w-md">
            {step === "identifier" && (
              <form onSubmit={sendOtp} className="space-y-6" data-testid="otp-request-form">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Step 1 of 2</div>
                  <h2 className="editorial text-3xl">Enter your phone or email</h2>
                  <p className="text-stone-600 text-sm mt-2">Use the number registered with your society.</p>
                </div>
                <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required autoFocus
                  data-testid="otp-identifier"
                  placeholder="98XXXXXXXX or your@email.com"
                  className="w-full h-16 px-5 rounded-2xl border-2 border-stone-200 bg-white text-xl focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
                <button type="submit" disabled={busy} data-testid="otp-request-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-4 text-lg font-medium transition-colors">
                  {busy ? "Sending…" : "Send OTP"} <ArrowRight size={20}/>
                </button>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={verifyOtp} className="space-y-6" data-testid="otp-verify-form">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Step 2 of 2</div>
                  <h2 className="editorial text-3xl">Enter the 6-digit OTP</h2>
                  <p className="text-stone-600 text-sm mt-2">Sent to <b>{identifier}</b>.</p>
                  {mockOtp && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-sand-200 border border-stone-300 px-3 py-1.5 text-xs text-stone-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta"/> Demo mode — OTP: <b className="font-mono">{mockOtp}</b>
                    </div>
                  )}
                </div>
                <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} required autoFocus
                  inputMode="numeric" maxLength={6}
                  data-testid="otp-code"
                  placeholder="••••••"
                  className="w-full h-20 px-5 rounded-2xl border-2 border-stone-200 bg-white text-4xl text-center tracking-[0.4em] focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none font-mono"/>
                <button type="submit" disabled={busy || otp.length < 6} data-testid="otp-verify-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-4 text-lg font-medium transition-colors">
                  {busy ? "Verifying…" : "Verify & sign in"} <ArrowRight size={20}/>
                </button>
                <button type="button" onClick={() => setStep("identifier")} className="w-full inline-flex items-center justify-center gap-2 text-sm text-stone-500 hover:text-ink">
                  <ArrowLeft size={14}/> Change number
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
